/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect } from 'react';
import ChatInterface from './ChatInterface';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AgentApi,
  WorkflowDetailDtoTypeEnum,
} from '@ink-ai/insight-service-sdk';
import { getApi } from '@ink-ai/portal/common/requestHelper';
import { useAsyncData } from '@ink-ai/portal/common/hooks/useAsyncData';
import { Box } from '@mui/system';
import { Breadcrumbs, LinearProgress, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchConversationHistory,
  fetchWorkflowConversations,
  invokeWorkflow,
  stopWorkflow,
  workflow,
  deleteWorkflowConversation,
} from '@ink-ai/portal/reducers/workflow';
import { RootState } from '@ink-ai/portal/reducers';
import { useTranslation } from 'react-i18next';
import { referenceActions } from '@ink-ai/portal/reducers/reference';
import { UploadFileToolbarItem } from './tools/UploadFileToolbarItem';
import { ChatToolItemProps } from './types';
import { OpenInWriterToolbarItem } from './tools/OpenInWriterToolbarItem';

export const InvokeWorkflow: React.FC = () => {
  const { id, conversationId } = useParams();
  const dispatch = useDispatch<any>();
  const { currentWorkflow, loading } = useSelector(
    (state: RootState) => state.workflow,
  );
  const navigate = useNavigate();
  const { data, isLoadingData } = useAsyncData(async () => {
    const workflowApi = await getApi(AgentApi);
    const workflowResp = await workflowApi.getAgentById(id);
    return {
      workflow: workflowResp.data,
    };
  }, [id]);

  useEffect(() => {
    if (id) {
      dispatch(fetchWorkflowConversations(id));
    }
  }, [id]);
  const { t } = useTranslation();

  useEffect(() => {
    if (
      data?.workflow?.config?.openingStatement &&
      currentWorkflow.messages.length === 0
    ) {
      dispatch(
        workflow.actions.appendMessage({
          id: 'init',
          content: data?.workflow?.config?.openingStatement,
          sender: 'ai',
          timestamp: new Date().getTime(),
          status: 'sent',
        }),
      );
    }
  }, [data?.workflow?.config?.openingStatement]);

  // clear on unmount
  useEffect(
    () => () => {
      dispatch(workflow.actions.clearCurrentWorkflow());
      dispatch(referenceActions.clearChecked());
    },
    [],
  );

  useEffect(() => {
    if (conversationId) {
      dispatch(workflow.actions.clearChatHistory());
      dispatch(fetchConversationHistory(conversationId));
      dispatch(workflow.actions.setConversationId(conversationId));
      dispatch(referenceActions.clearChecked());
    }
  }, [conversationId]);

  const handleSendMessage = async (content: string) => {
    dispatch(
      invokeWorkflow({
        query: content,
        workflowId: id,
      }),
    );
  };

  const handleNewChat = () => {
    navigate(`/workflows/${id}/invoke`);
    // Clear current chat history first
    dispatch(workflow.actions.clearChatHistory());

    // Then add the opening statement
    if (data?.workflow?.config?.openingStatement) {
      dispatch(
        workflow.actions.appendMessage({
          id: '1',
          content: data?.workflow?.config?.openingStatement,
          sender: 'ai',
          timestamp: new Date().getTime(),
          status: 'sent',
        }),
      );
    }
  };

  if (isLoadingData) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t('workflows.navigation.home')}</Link>
        <Link to="/workflows">{t('workflows.navigation.workflows')}</Link>
        <Typography color="textPrimary">{data?.workflow?.name}</Typography>
      </Breadcrumbs>
      <ChatInterface
        messages={currentWorkflow.messages}
        onSendMessage={handleSendMessage}
        isTyping={currentWorkflow.waitingResponse}
        isLoading={currentWorkflow.running}
        title={data?.workflow?.name}
        onNewChat={handleNewChat}
        onStop={() => dispatch(stopWorkflow(id))}
        history={currentWorkflow.conversations.data.map((conv) => ({
          ...conv,
          selected:
            conversationId === conv.id ||
            currentWorkflow.conversationId === conv.id,
        }))}
        onSelectHistory={(conversationId) => {
          navigate(`/workflows/${id}/invoke/${conversationId}`);
        }}
        onDeleteHistory={(deletedConversationId) => {
          if (!id) return;
          dispatch(
            deleteWorkflowConversation({
              workflowId: id,
              conversationId: deletedConversationId,
              onSuccess: () => {
                if (deletedConversationId === currentWorkflow.conversationId) {
                  handleNewChat();
                }
              },
            }),
          );
        }}
        historyLoading={currentWorkflow.conversations.loading}
        messagesLoading={currentWorkflow.loadingMessages}
        hasMore={currentWorkflow.hasMore}
        onLoadMore={() => dispatch(fetchConversationHistory(conversationId))}
        toolbarItems={[
          (props) => <UploadFileToolbarItem {...props} />,
          ...(data.workflow.type === WorkflowDetailDtoTypeEnum.Dify
            ? [
                (props: ChatToolItemProps) => (
                  <OpenInWriterToolbarItem {...props} />
                ),
              ]
            : []),
        ]}
      />
    </>
  );
};
